import {
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { useTerminalConfig } from '../../context/config-context';
import { useAgentState } from '../../providers/contexts';
import { DownloadIcon, LogoutIcon } from './icons';
import { MenuItem, MenuPositioned } from './menu';
import avatar1 from '../../assets/avatars/avatar-1.avif';
import avatar2 from '../../assets/avatars/avatar-2.avif';
import avatar3 from '../../assets/avatars/avatar-3.avif';
import avatar4 from '../../assets/avatars/avatar-4.avif';
import avatar5 from '../../assets/avatars/avatar-5.avif';

// ─────────────────────────────────────────────────────────────────────────
// Account identity: prefer the host-supplied profile (`config.user` —
// display name / email / avatar, e.g. from Clerk in the cloud flow). The
// gateway-URL derivation below is the FALLBACK for hosts without a profile
// (manual pairing against a per-user gateway like
// `mingjun-4275289e.terminal.dime.gg`). Behind the shared gateway-proxy the
// URL is the same for everyone (`gateway.dimeterminal.ai`), so URL-derived
// names there are wrong — cloud hosts must pass `config.user`.
// ─────────────────────────────────────────────────────────────────────────

// FALLBACK: derive a display username from the gateway URL. Rule: take the
// first subdomain label, then everything before its first `-`, capitalized.
// e.g. `wss://mingjun-4275289e.terminal.dime.gg` ⇒ `Mingjun`.
function deriveUsername(url: string | null | undefined): string {
  if (url == null || url.length === 0) return '';
  const host = url.replace(/^wss?:\/\//, '').split(/[/:?#]/)[0] ?? '';
  const subdomain = host.split('.')[0] ?? '';
  const name = subdomain.split('-')[0] ?? '';
  return name.length > 0 ? name.charAt(0).toUpperCase() + name.slice(1) : '';
}

// FALLBACK: placeholder profile pictures for hosts without a real avatar
// (`config.user.avatarUrl` wins when present). `avatarFor` hashes its seed
// (djb2) into a stable bucket so the same account always lands on the same
// picture.
const AVATARS: readonly string[] = [
  avatar1,
  avatar2,
  avatar3,
  avatar4,
  avatar5,
];

function avatarFor(seed: string | null | undefined): string | null {
  if (seed == null || seed.length === 0) return null;
  let h = 5381;
  for (let i = 0; i < seed.length; i += 1) {
    h = (Math.imul(h, 33) ^ seed.charCodeAt(i)) >>> 0;
  }
  return AVATARS[h % AVATARS.length] ?? null;
}

interface SidebarAccountButtonProps {
  /** Gateway URL — identity is derived from it (see TEMP notes above). */
  readonly gatewayLabel: string;
  /** Host "log out / unpair" action; the menu hides the item when omitted. */
  readonly onLogout?: () => void;
}

// Pinned footer account button at the bottom of the sidebar, plus its popup
// menu (gateway label + connection actions + "Log Out"). Owns its own
// open/close state. The avatar carries a status dot driven by the live agent
// connection so the user can see at a glance whether the gateway is up.
export default function SidebarAccountButton({
  gatewayLabel,
  onLogout,
}: SidebarAccountButtonProps) {
  const state = useAgentState();
  const { onDownloadApp, user } = useTerminalConfig();
  // Host profile wins; the gateway-URL derivation is the no-profile fallback
  // (see the header note). Display-name fallback chain: profile name →
  // email local part → URL-derived.
  const username = useMemo(() => {
    const fromProfile = user?.displayName?.trim() ?? '';
    if (fromProfile.length > 0) return fromProfile;
    const emailLocal = user?.email?.split('@')[0]?.trim() ?? '';
    if (emailLocal.length > 0) return emailLocal;
    return deriveUsername(gatewayLabel);
  }, [user?.displayName, user?.email, gatewayLabel]);
  const avatarLetter = username.charAt(0).toUpperCase() || '?';
  const avatarSrc = useMemo(() => {
    if (user?.avatarUrl != null && user.avatarUrl.length > 0) {
      return user.avatarUrl;
    }
    // Stable placeholder: seed off the identity we display, not the shared
    // proxy URL (which would give every cloud user the same picture).
    return avatarFor(user?.email ?? gatewayLabel);
  }, [user?.avatarUrl, user?.email, gatewayLabel]);
  // Menu header + hover title: the user's email beats the gateway URL (behind
  // the shared proxy the URL identifies infrastructure, not the account).
  const accountLabel =
    user?.email != null && user.email.length > 0 ? user.email : gatewayLabel;
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const [accountMenuRect, setAccountMenuRect] = useState<DOMRect | null>(null);
  const toggleAccountMenu = useCallback(() => {
    setAccountMenuRect((cur) =>
      cur != null
        ? null
        : (accountButtonRef.current?.getBoundingClientRect() ?? null),
    );
  }, []);

  return (
    <>
      <AccountBar>
        <AccountButton
          ref={accountButtonRef}
          data-account-button
          type="button"
          onClick={toggleAccountMenu}
          title={accountLabel}
          aria-haspopup="menu"
          aria-expanded={accountMenuRect != null}
        >
          <AvatarSlot>
            {avatarSrc != null ? (
              <AccountAvatarImg src={avatarSrc} alt="" />
            ) : (
              <AccountAvatar>{avatarLetter}</AccountAvatar>
            )}
            <StatusDot $status={state.status} />
          </AvatarSlot>
          <AccountName>
            {username.length > 0 ? username : 'Account'}
          </AccountName>
          <ChevronIcon $open={accountMenuRect != null} />
        </AccountButton>
      </AccountBar>
      {accountMenuRect != null && (
        <AccountMenu
          anchorRect={accountMenuRect}
          accountLabel={accountLabel}
          hasLogout={onLogout != null}
          hasDownload={onDownloadApp != null}
          onClose={() => setAccountMenuRect(null)}
          onDownload={() => {
            onDownloadApp?.();
            setAccountMenuRect(null);
          }}
          onLogout={() => {
            onLogout?.();
            setAccountMenuRect(null);
          }}
        />
      )}
    </>
  );
}

// Popup for the sidebar account button. Anchors directly above the button
// (it lives at the very bottom of the sidebar), clamps into the viewport, and
// closes on click-away / Escape. Mirrors the FolderContextMenu portal pattern.
function AccountMenu({
  anchorRect,
  accountLabel,
  hasLogout,
  hasDownload,
  onClose,
  onDownload,
  onLogout,
}: {
  readonly anchorRect: DOMRect;
  /** Header line: user email when the host supplies one, else gateway URL. */
  readonly accountLabel: string;
  readonly hasLogout: boolean;
  readonly hasDownload: boolean;
  readonly onClose: () => void;
  readonly onDownload: () => void;
  readonly onLogout: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number }>(() => ({
    x: anchorRect.left,
    y: anchorRect.top,
  }));

  useEffect(() => {
    function onClickAway(e: globalThis.MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest('[data-account-menu]') != null) return;
      // The button toggles the menu itself; ignore clicks on it here so the
      // toggle isn't immediately undone by this click-away handler.
      if (target.closest('[data-account-button]') != null) return;
      onClose();
    }
    function onEsc(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('mousedown', onClickAway, true);
    window.addEventListener('keydown', onEsc, true);
    return () => {
      window.removeEventListener('mousedown', onClickAway, true);
      window.removeEventListener('keydown', onEsc, true);
    };
  }, [onClose]);

  // Place the menu just above the anchor, left-aligned, clamped on-screen.
  // Measured after layout so the menu height is known before paint.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el == null) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const gap = 6;
    const maxX = window.innerWidth - rect.width - margin;
    setPos({
      x: Math.max(margin, Math.min(anchorRect.left, maxX)),
      y: Math.max(margin, anchorRect.top - rect.height - gap),
    });
  }, [anchorRect]);

  return createPortal(
    <MenuPositioned
      ref={ref}
      data-account-menu
      role="menu"
      style={{
        left: pos.x,
        top: pos.y,
        minWidth: Math.max(180, anchorRect.width),
      }}
    >
      {accountLabel.length > 0 && (
        <AccountMenuHeader title={accountLabel}>
          {accountLabel}
        </AccountMenuHeader>
      )}
      {hasDownload && (
        <MenuItem
          type="button"
          role="menuitem"
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onDownload();
          }}
        >
          <DownloadIcon />
          Download App
        </MenuItem>
      )}
      {hasLogout && (
        <MenuItem
          $danger
          type="button"
          role="menuitem"
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onLogout();
          }}
        >
          <LogoutIcon />
          Log Out
        </MenuItem>
      )}
    </MenuPositioned>,
    document.body,
  );
}

// Pinned footer holding the account button. Sticks to the bottom of the
// scrolling sidebar (Container is the scroll parent); `margin-top: auto`
// drops it to the bottom when the chats list is short.
const AccountBar = styled.div`
  position: sticky;
  bottom: 0;
  margin-top: auto;
  flex-shrink: 0;
  padding: 8px;
  background: var(--bg-color-page);
  border-top: 1px solid var(--divider-color);
`;

// Matches the rounded-corner radius modern Chromium PWA windows draw at
// their outer chrome — picking the same value here makes the account bar
// read as part of the window's own frame rather than a contrasting pill.
const AccountButton = styled.button`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 12px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg);
  }
`;

// Right-aligned chevron that flips when the popup opens. Sits at the end
// of the row so it reads as a disclosure indicator rather than competing
// with the account name.
function ChevronIcon({ $open }: { readonly $open: boolean }) {
  return (
    <ChevronIconSvg $open={$open} viewBox="0 0 12 12" aria-hidden="true">
      <path
        d="M2.5 4.5l3.5 3.5 3.5-3.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </ChevronIconSvg>
  );
}

const ChevronIconSvg = styled.svg<{ $open: boolean }>`
  margin-left: auto;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  color: var(--text-secondary);
  transition: transform 0.15s ease;
  transform: ${(p) => (p.$open ? 'rotate(180deg)' : 'rotate(0)')};
`;

// Wraps the avatar so the connection-status dot can absolute-position
// itself in the bottom-right corner without affecting layout.
const AvatarSlot = styled.span`
  position: relative;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
`;

// Connection-status indicator overlaid on the avatar. Same color rules as
// the old top-bar dot — accent when authenticated/connected, amber while
// the handshake is in flight, error on a failed connect, muted otherwise.
const StatusDot = styled.span<{ $status: string }>`
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 2px solid var(--bg-color-page);
  background: ${({ $status }) => {
    switch ($status) {
      case 'connected':
      case 'authenticated':
        return 'var(--accent-color)';
      case 'connecting':
      case 'authenticating':
        return 'var(--pending-color)';
      case 'error':
        return 'var(--error-color)';
      default:
        return 'var(--text-secondary)';
    }
  }};
`;

// Circular monogram avatar — first letter of the username, Google-style.
const AccountAvatar = styled.span`
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--accent-color);
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
`;

// Image variant of the avatar — same circular footprint as the monogram.
const AccountAvatarImg = styled.img`
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

const AccountName = styled.span`
  font-size: 13px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Muted gateway-URL line at the top of the account menu (where the old
// top-bar gateway label used to live).
const AccountMenuHeader = styled.div`
  padding: 6px 8px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--divider-color);
  font-size: 0.7rem;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
